import { useEffect, useState } from 'react'
import CloudQueueRoundedIcon from '@mui/icons-material/CloudQueueRounded'
import { useNls } from '../../nls/nlsProvider'
import type { HelperAssignedItem } from '../../services/helper/helperAssignedItemsService'
import type { HelperWorkspaceSummary } from '../../services/helper/helperStore'
import type { HelperOpenBlocker } from '../../types/helperBlocker'
import type { HelperReadinessModel } from './helperReadinessModel'
import {
  normalizeHelperFieldSubtab,
  type HelperFieldSubtab,
} from './helperFieldModel'
import { HelperFieldSubtabStrip } from './HelperFieldSubtabStrip'
import { HelperFieldToday } from './HelperFieldToday'
import { HelperScanTab } from './HelperScanTab'
import { HelperStockCountTab } from './HelperStockCountTab'
import { HelperQuotaPanel } from './HelperQuotaPanel'
import { HelperReliabilityPanel } from './HelperReliabilityPanel'
import { ClockCard } from '../Timesheets/ClockCard'
import type { Workspace } from '../../types/workspace'

export type HelperFieldTabProps = {
  workspaceId: string
  workspaces: HelperWorkspaceSummary[]
  workspace?: Workspace | null
  items: HelperAssignedItem[]
  actor: { id: string; name: string } | null
  subtab?: HelperFieldSubtab
  initialSubtab?: HelperFieldSubtab
  visibleSubtabs?: HelperFieldSubtab[]
  onSubtabChange?: (subtab: HelperFieldSubtab) => void
  readinessByItemId?: Map<string, HelperReadinessModel>
  openBlockers?: HelperOpenBlocker[]
  onOpenItem?: (itemId: string) => void
  unsentCount?: number
  workspaceName?: string
  hideSubtabs?: boolean
}

export const HelperFieldTab = ({
  workspaceId,
  workspaces,
  workspace,
  items,
  actor,
  subtab: controlledSubtab,
  initialSubtab = 'TODAY',
  visibleSubtabs,
  onSubtabChange,
  readinessByItemId,
  openBlockers,
  onOpenItem,
  unsentCount = 0,
  workspaceName,
  hideSubtabs = false,
}: HelperFieldTabProps) => {
  const { t } = useNls()
  const [internalSubtab, setInternalSubtab] = useState<HelperFieldSubtab>(() =>
    normalizeHelperFieldSubtab(initialSubtab),
  )

  const activeSubtab = normalizeHelperFieldSubtab(controlledSubtab ?? internalSubtab)

  const setActiveSubtab = (next: HelperFieldSubtab) => {
    if (controlledSubtab === undefined) setInternalSubtab(next)
    onSubtabChange?.(next)
  }

  useEffect(() => {
    if (controlledSubtab === undefined) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs initial subtab when uncontrolled
      setInternalSubtab(normalizeHelperFieldSubtab(initialSubtab))
    }
  }, [controlledSubtab, initialSubtab])

  const effectiveWorkspaceName =
    workspaceName ||
    workspaces.find((w) => w.id === workspaceId)?.name ||
    t('NLSHelperFieldDefaultWorkspace')

  return (
    <section className="flex-1 flex flex-col gap-2 min-h-0" data-testid="helper-field-tab">
      {/* Direction A Ledger App Bar */}
      <div className="flex flex-col gap-1 px-4 pt-2 pb-0">
        <div className="flex items-center justify-between font-mono text-[11px] font-medium tracking-wider uppercase text-[var(--muted)]">
          <span className="truncate">{effectiveWorkspaceName}</span>
          {unsentCount > 0 ? (
            <span
              className="inline-flex items-center gap-1 text-[var(--warning)] font-semibold tracking-normal lowercase"
              data-testid="helper-field-queued-badge"
            >
              <CloudQueueRoundedIcon style={{ fontSize: 14 }} />
              {unsentCount} {t('NLSHelperFieldQueuedCount')}
            </span>
          ) : null}
        </div>
        <h2 className="text-xl font-bold tracking-tight text-[var(--text-strong)]">
          {t('NLSHelperTabField')}
        </h2>
      </div>

      {!hideSubtabs ? (
        <HelperFieldSubtabStrip
          subtabs={visibleSubtabs ?? ['TODAY', 'SCAN', 'COUNT', 'ME']}
          active={activeSubtab}
          onChange={setActiveSubtab}
          t={t}
        />
      ) : null}

      <div className="flex-1 overflow-y-auto min-h-0">
        {activeSubtab === 'TODAY' ? (
          <HelperFieldToday
            items={items}
            readinessByItemId={readinessByItemId}
            openBlockers={openBlockers}
            onOpenItem={onOpenItem}
            workspaceId={workspaceId}
          />
        ) : activeSubtab === 'SCAN' ? (
          /* SCAN subtab mounts existing HelperScanTab unchanged per FI14.b */
          <HelperScanTab
            workspaceId={workspaceId}
            workspaces={workspaces}
            items={items}
            actor={actor}
          />
        ) : activeSubtab === 'COUNT' ? (
          /* COUNT subtab mounts existing HelperStockCountTab unchanged per FI14.b */
          <HelperStockCountTab workspaceId={workspaceId} actor={actor} />
        ) : (
          /* ME subtab mounts ClockCard, existing Quota and Reliability panels */
          <div className="flex flex-col gap-3 p-4">
            {workspace ? (
              <ClockCard workspace={workspace} actorRole="HELPER" />
            ) : null}
            <HelperQuotaPanel workspaceId={workspaceId} actor={actor} items={items} />
            <HelperReliabilityPanel workspaceId={workspaceId} actor={actor} items={items} />
          </div>
        )}
      </div>
    </section>
  )
}
